import type { HTMLAttributes, Ref } from "react";

import { cn } from "../../utils";
import { Badge, type BadgeVariant } from "../Badge";

export type SwitchType = "market" | "option";
export type SwitchMarketValue = "buy" | "sell";
export type SwitchOptionValue = "call" | "put";
export type SwitchValue = SwitchMarketValue | SwitchOptionValue;

interface BaseSwitchProps<Type, Value> extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "onChange"
> {
  ref?: Ref<HTMLDivElement>;
  type: Type;
  value: Value;
  disabled?: boolean;
  onChange?: (value: Value) => void;
}

export type SwitchProps =
  | BaseSwitchProps<"market", SwitchMarketValue>
  | BaseSwitchProps<"option", SwitchOptionValue>;

const getBadgeVariant = (option: SwitchValue): BadgeVariant => {
  return ["buy", "sell"].includes(option) ? (option as BadgeVariant) : "gray";
};

export const Switch = ({
  type,
  value,
  disabled,
  className,
  tabIndex,
  onChange,
  ref,
  ...props
}: SwitchProps) => {
  const isMarket = type === "market";
  const options: SwitchValue[] = isMarket ? ["buy", "sell"] : ["call", "put"];

  const handleChange = (option: SwitchValue) => {
    if (isMarket) onChange?.(option as SwitchMarketValue);
    else onChange?.(option as SwitchOptionValue);
  };

  return (
    <div
      className={cn(
        "ui:flex ui:w-fit ui:items-center ui:gap-0.5 ui:rounded-sm ui:bg-buttonFill ui:p-1 ui:font-grotesk ui:text-xs ui:font-medium",
        { "ui:pointer-events-none": disabled },
        className,
      )}
      {...props}
      ref={ref}
    >
      {options.map((option) => (
        <button
          key={option}
          disabled={disabled}
          tabIndex={tabIndex}
          className={cn("ui:capitalize", {
            "ui:cursor-pointer": !disabled,
          })}
          onClick={() => {
            handleChange(option);
          }}
        >
          <Badge
            variant={getBadgeVariant(option)}
            className={cn("ui:h-6", {
              "ui:text-textSecondary ui:before:opacity-0": option !== value,
              "ui:hover:text-textPrimary": !disabled,
            })}
          >
            {option}
          </Badge>
        </button>
      ))}
    </div>
  );
};
